import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { AgoraEngine } from '../rtc/agoraEngine'
import { DeviceList } from '../types'

interface DevicesContextValue {
  devices: DeviceList
  selectedCameraId: string
  selectedMicrophoneId: string
  refreshDevices: () => Promise<void>
  changeCamera: (deviceId: string) => Promise<void>
  changeMicrophone: (deviceId: string) => Promise<void>
}

const DevicesContext = createContext<DevicesContextValue | null>(null)

interface Props {
  engine: AgoraEngine
  children: React.ReactNode
}

export function DevicesProvider({ engine, children }: Props) {
  const [devices, setDevices] = useState<DeviceList>({
    cameras: [],
    microphones: [],
  })
  const [selectedCameraId, setSelectedCameraId] = useState('')
  const [selectedMicrophoneId, setSelectedMicrophoneId] = useState('')

  const refreshDevices = useCallback(async () => {
    const next = await engine.listDevices()
    setDevices(next)
    if (!selectedCameraId && next.cameras.length > 0) {
      setSelectedCameraId(next.cameras[0].deviceId)
    }
    if (!selectedMicrophoneId && next.microphones.length > 0) {
      setSelectedMicrophoneId(next.microphones[0].deviceId)
    }
  }, [engine, selectedCameraId, selectedMicrophoneId])

  const changeCamera = useCallback(
    async (deviceId: string) => {
      await engine.switchCamera(deviceId)
      setSelectedCameraId(deviceId)
    },
    [engine]
  )

  const changeMicrophone = useCallback(
    async (deviceId: string) => {
      await engine.switchMicrophone(deviceId)
      setSelectedMicrophoneId(deviceId)
    },
    [engine]
  )

  useEffect(() => {
    void refreshDevices()

    const handleDeviceChange = () => {
      void refreshDevices()
    }

    navigator.mediaDevices?.addEventListener('devicechange', handleDeviceChange)
    return () => {
      navigator.mediaDevices?.removeEventListener(
        'devicechange',
        handleDeviceChange
      )
    }
  }, [refreshDevices])

  return (
    <DevicesContext.Provider
      value={{
        devices,
        selectedCameraId,
        selectedMicrophoneId,
        refreshDevices,
        changeCamera,
        changeMicrophone,
      }}
    >
      {children}
    </DevicesContext.Provider>
  )
}

export function useDevices() {
  const context = useContext(DevicesContext)
  if (!context) {
    throw new Error('useDevices precisa ser usado com DevicesProvider')
  }
  return context
}
